/**
 * Tiny helper to convert a YouTube or Vimeo URL into an `<iframe src>`
 * suitable for embedding directly. Returns null for anything we don't
 * recognize, so callers can decide whether to fall back to a plain link.
 *
 * Supports:
 *   - youtube.com/watch?v=ID
 *   - youtu.be/ID
 *   - youtube.com/embed/ID  (already embed form, pass through)
 *   - youtube.com/shorts/ID
 *   - vimeo.com/ID
 *   - player.vimeo.com/video/ID
 *
 * Strips any query params we don't recognize to avoid carrying auto-play
 * tokens etc. between coach-pasted URLs and athlete playback.
 */
export function videoEmbedUrl(raw) {
    if (!raw || typeof raw !== "string") return null;
    const url = raw.trim();
    if (!url) return null;
    try {
        const u = new URL(url);
        const host = u.hostname.replace(/^www\./, "").toLowerCase();
        // ----- YouTube -----
        if (host === "youtu.be") {
            const id = u.pathname.slice(1).split("/")[0];
            if (id) return `https://www.youtube.com/embed/${id}`;
        }
        if (host.endsWith("youtube.com") || host === "youtube.com") {
            if (u.pathname === "/watch") {
                const id = u.searchParams.get("v");
                if (id) return `https://www.youtube.com/embed/${id}`;
            }
            const m = u.pathname.match(/^\/(embed|shorts)\/([\w-]{6,})/);
            if (m) return `https://www.youtube.com/embed/${m[2]}`;
        }
        // ----- Vimeo -----
        if (host === "vimeo.com") {
            const id = u.pathname.split("/").filter(Boolean)[0];
            if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
        }
        if (host === "player.vimeo.com") {
            const m = u.pathname.match(/^\/video\/(\d+)/);
            if (m) return `https://player.vimeo.com/video/${m[1]}`;
        }
    } catch {
        return null;
    }
    return null;
}
